/**
 * Partner Invitation Seed Data
 *
 * Contains sample B2B partner invitations with contract packages
 * for seeding via the partnerInvitations.create tRPC endpoint.
 *
 * DEPENDENCY: Requires businesses and users to exist.
 *
 * @see Issue #705 - Contract-Driven Partner Invitations
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// SEED IDs
// =============================================================================

export const PARTNER_INVITATION_IDS = {
  /** Referral partner invitation — Sunshine Mortgage */
  REFERRAL_SUNSHINE: seedId('partner-inv-referral', 1),
  /** Broker partner invitation — Premier Realty */
  BROKER_PREMIER: seedId('partner-inv-broker', 2),
  /** Wholesale partner invitation — Gulf Coast Lending */
  WHOLESALE_GULF: seedId('partner-inv-wholesale', 3),
  /** Referral invitation for 1Sharpe Capital */
  REFERRAL_1SHARPE: seedId('partner-inv-referral', 4),
} as const;

// =============================================================================
// DATA TYPES
// =============================================================================

export interface PartnerInvitationSeedData {
  id: string;
  businessId: string;
  invitedCompanyName: string;
  invitedEmail: string;
  invitedContactName: string;
  invitedPhone?: string;
  partnerType: 'referral' | 'white_label';
  agreementType: 'referral' | 'correspondent' | 'wholesale' | 'white_label';
  contractTerms: {
    commissionType: 'percentage' | 'flat_fee' | 'tiered';
    commissionRate?: number;
    flatFeeAmount?: number;
    commissionTrigger: 'funded' | 'term_sheet' | 'application';
    paymentSchedule: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
    agreementDuration: string;
    exclusivity?: boolean;
    territory?: string;
    specialTerms?: string;
    renewalTerms?: 'auto_renew' | 'manual_renewal';
  };
  expiresInDays: number;
}

// =============================================================================
// INVITATION DATA
// =============================================================================

export const PARTNER_INVITATIONS: Record<keyof typeof PARTNER_INVITATION_IDS, PartnerInvitationSeedData> = {
  REFERRAL_SUNSHINE: {
    id: PARTNER_INVITATION_IDS.REFERRAL_SUNSHINE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    invitedCompanyName: 'Sunshine Mortgage Group',
    invitedEmail: 'anna.broker@sunshinemortgage.com',
    invitedContactName: 'Anna Hernandez',
    invitedPhone: '(561) 555-0299',
    partnerType: 'referral',
    agreementType: 'referral',
    contractTerms: {
      commissionType: 'percentage',
      commissionRate: 1.5,
      commissionTrigger: 'funded',
      paymentSchedule: 'MONTHLY',
      agreementDuration: '12',
      exclusivity: false,
      territory: JSON.stringify(['FL']),
      renewalTerms: 'auto_renew',
    },
    expiresInDays: 14,
  },

  BROKER_PREMIER: {
    id: PARTNER_INVITATION_IDS.BROKER_PREMIER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    invitedCompanyName: 'Premier Realty Partners',
    invitedEmail: 'carlos.martinez@premierrealty.com',
    invitedContactName: 'Carlos Martinez',
    invitedPhone: '(954) 555-0177',
    partnerType: 'white_label',
    agreementType: 'correspondent',
    contractTerms: {
      commissionType: 'tiered',
      commissionRate: 2.0,
      commissionTrigger: 'funded',
      paymentSchedule: 'MONTHLY',
      agreementDuration: '24',
      exclusivity: false,
      territory: JSON.stringify(['FL', 'GA']),
      specialTerms: 'Tiered rates: 1.5% for first $5M funded volume, 2.0% for $5M-$10M, 2.5% for $10M+',
      renewalTerms: 'auto_renew',
    },
    expiresInDays: 21,
  },

  WHOLESALE_GULF: {
    id: PARTNER_INVITATION_IDS.WHOLESALE_GULF,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    invitedCompanyName: 'Gulf Coast Lending Services',
    invitedEmail: 'david.originator@gulfcoastlending.com',
    invitedContactName: 'David Chen',
    invitedPhone: '(239) 555-0344',
    partnerType: 'white_label',
    agreementType: 'wholesale',
    contractTerms: {
      commissionType: 'percentage',
      commissionRate: 0.75,
      commissionTrigger: 'funded',
      paymentSchedule: 'BIWEEKLY',
      agreementDuration: '36',
      exclusivity: true,
      territory: JSON.stringify(['FL']),
      specialTerms: 'Exclusive wholesale relationship for SW Florida market. Quarterly volume reviews.',
      renewalTerms: 'manual_renewal',
    },
    expiresInDays: 30,
  },

  REFERRAL_1SHARPE: {
    id: PARTNER_INVITATION_IDS.REFERRAL_1SHARPE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    invitedCompanyName: 'Atlanta Property Advisors',
    invitedEmail: 'lisa.williams@atlantapropadvisors.com',
    invitedContactName: 'Lisa Williams',
    invitedPhone: '(404) 555-0188',
    partnerType: 'referral',
    agreementType: 'referral',
    contractTerms: {
      commissionType: 'flat_fee',
      flatFeeAmount: 2500,
      commissionTrigger: 'funded',
      paymentSchedule: 'MONTHLY',
      agreementDuration: '12',
      exclusivity: false,
      renewalTerms: 'auto_renew',
    },
    expiresInDays: 14,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllPartnerInvitations(): PartnerInvitationSeedData[] {
  return Object.values(PARTNER_INVITATIONS);
}

export function getPartnerInvitationsByBusiness(businessId: string): PartnerInvitationSeedData[] {
  return Object.values(PARTNER_INVITATIONS).filter((inv) => inv.businessId === businessId);
}
